import { useTranslation } from 'react-i18next';
import { AppContext } from '../../services/AppContext';
import MultiSelector from '../../components/global/MultiSelector';
import { React, useState, useContext, useRef, useEffect } from 'react';

const SubscriptionEditor = ({ closeModal, owner, subscription }) => {
    const { t } = useTranslation();
    const formatDate = (date) => {
        if (!date) return '';
        const d = new Date(date);
        return d.toISOString().split('T')[0];
    };
    const { apiClient, notify } = useContext(AppContext);
    const today = new Date();
    const [newSubscription, setNewSubscription] = useState({
        classesLeft: '',
        cost: '',
        discount: subscription?.discount * 100 || '',
        managersShare: subscription?.managersShare || '',
        starts: formatDate(subscription?.starts) || today.toISOString().split('T')[0],
        expires: formatDate(subscription?.expires) || new Date(today.getFullYear(), today.getMonth() + 1, today.getDate() + 1).toISOString().split('T')[0],
        owner: owner?.name ?? '',
        ownerId: owner?.id ?? null
    });
    const [suggestions, setSuggestions] = useState([]);
    const dropdownRef = useRef(null);
    const [subscriptionPresetData, setSubscriptionPresetData] = useState([]);
    const [selectedPreset, setSelectedPreset] = useState();
    const [selectedTagIds, setSelectedTagIds] = useState(subscription?.tags?.map(tag => tag.id) || []);
    const [selectedTags, setSelectedTags] = useState(subscription?.tags || []);
    const [tagData, setTagData] = useState([]);
    const updating = !!subscription;

    const fetchPresets = async () => {
        const data = await apiClient.getSubscriptionPresets();

        if (!data.hasErrors) {
            setSubscriptionPresetData(data);
        }
    };

    const fetchTags = async () => {
        const data = await apiClient.getTags();

        if (!data.hasErrors) {
            setTagData(data);
        }
    };

    const handleSaveSubscription = async () => {
        if (subscription) {
            newSubscription.id = subscription.id;
        } else {
            newSubscription.classesLeft = parseInt(newSubscription.classesLeft) || 1;
            newSubscription.cost = parseInt(newSubscription.cost) || 0;
        }

        if (!selectedTags.some(tg => tg.name === t('common.discount'))) {
            newSubscription.discount = 0;
        }

        newSubscription.tags = selectedTagIds;

        const result = updating
            ? await apiClient.updateSubscription({
                ...newSubscription,
                discount: newSubscription.discount / 100,
            })
            : await apiClient.createSubscription({
                ...newSubscription,
                managersShare: (parseInt(newSubscription.managersShare) || 0),
                discount: newSubscription.discount / 100,
            });

        if (result.hasErrors) {
            return;
        }

        notify(
            t(`pages.subscriptions.notifications.${updating ? 'updated' : 'created'}`),
            'success'
        );
        closeModal(true);
        setNewSubscription({});
    };

    const handleCancel = () => {
        closeModal(false);
        setNewSubscription({});
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setNewSubscription((prev) => ({
            ...prev,
            [name]: value,
        }));

        if (name === 'owner' && value.trim() && value !== '') {
            fetchSuggestions(value);
        } else {
            setSuggestions([]);
        }
    };

    const fetchSuggestions = async (query) => {
        const data = await apiClient.getUsers({
            name: query,
            role: 2
        }, false);

        if (data.hasErrors) return;

        setSuggestions(data || []);
    };

    const handleSuggestionClick = (suggestion) => {
        setNewSubscription((prev) => ({
            ...prev,
            ownerId: suggestion.id,
            owner: suggestion.name,
        }));
        setSuggestions([]);
    };

    useEffect(() => {
        if (!updating) {
            fetchPresets();
        }
        fetchTags();
    }, []);

    const handleSelectionChange = (e) => {
        const preset = subscriptionPresetData.find(preset => preset.id === e.target.value);

        setSelectedPreset(preset);

        if (preset?.name !== t('common.special')) {
            setNewSubscription((prev) => ({
                ...prev,
                classesLeft: preset.total,
                cost: preset.cost,
            }));
        }

        setSelectedTagIds(preset.tags.map(tag => tag.id));
    };

    useEffect(() => {
        if (subscriptionPresetData.length > 0) {
            handleSelectionChange({
                target: {
                    value: subscriptionPresetData[0].id
                }
            });
        }
    }, [subscriptionPresetData]);

    useEffect(() => {
        setSelectedTags(tagData?.filter(tag => selectedTagIds.includes(tag.id)) || []);
    }, [selectedTagIds, tagData]);

    return (
        <div className="modal">
            <div className="modal-content">
                <h3>{updating ? t('pages.subscriptions.headers.edit') : t('pages.subscriptions.headers.new')}</h3>

                <h5>{t('pages.subscriptions.fields.starts')}</h5>
                <input
                    type="date"
                    name="starts"
                    placeholder={t('pages.subscriptions.placeholders.starts')}
                    className="width-100p"
                    value={newSubscription.starts}
                    onChange={handleChange}
                />

                <h5>{t('pages.subscriptions.fields.expires')}</h5>
                <input
                    type="date"
                    name="expires"
                    placeholder={t('pages.subscriptions.placeholders.expires')}
                    className="width-100p"
                    value={newSubscription.expires}
                    onChange={handleChange}
                />

                {!updating && (
                    <div ref={dropdownRef}>
                        <input
                            type="text"
                            name="owner"
                            placeholder={t('pages.subscriptions.placeholders.owner')}
                            value={newSubscription.owner}
                            onChange={handleChange}
                            autoComplete="off"
                        />

                        {suggestions.length > 0 && (
                            <ul className="suggestions-dropdown">
                                {suggestions.map((suggestion) => (
                                    <li
                                        key={suggestion.id}
                                        onClick={() => handleSuggestionClick(suggestion)}
                                    >
                                        {suggestion.name}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}

                {!updating && (
                    <div style={{ marginBottom: '10px' }}>
                        <select
                            id="month"
                            value={selectedPreset?.id}
                            onChange={handleSelectionChange}
                            style={{ height: '36px', fontSize: '16px', padding: '5px' }}
                        >
                            {subscriptionPresetData.map(preset => (
                                <option key={preset.id} value={preset.id}>
                                    {preset.name} {preset.cost !== 0 ? `${preset.cost}` : ''}
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                {selectedPreset?.name === t('common.special') && !updating && (
                    <div style={{ marginTop: '5px' }}>
                        <input
                            type="number"
                            name="classesLeft"
                            placeholder={t('pages.subscriptions.placeholders.total')}
                            value={newSubscription.classesLeft}
                            onChange={handleChange}
                        />

                        <input
                            type="number"
                            name="cost"
                            placeholder={t('pages.subscriptions.placeholders.cost')}
                            value={newSubscription.cost}
                            onChange={handleChange}
                        />
                    </div>
                )}

                {selectedTags.some(tg => tg.name === t('common.discount')) && (
                    <div style={{ marginTop: '5px' }}>
                        <input
                            type="number"
                            name="discount"
                            placeholder={`${t('common.discount')} %`}
                            value={newSubscription.discount}
                            onChange={handleChange}
                        />
                    </div>
                )}

                {!updating && (
                    <div style={{ marginTop: '5px' }}>
                        <input
                            type="number"
                            name="managersShare"
                            placeholder={`${t('common.managers_share')} %`}
                            value={newSubscription.managersShare}
                            onChange={handleChange}
                        />
                    </div>
                )}

                <div className="mg-b-1">
                    <MultiSelector
                        options={tagData}
                        selection={selectedTagIds}
                        onChange={(selection) => setSelectedTagIds(selection)}
                    />
                </div>

                <div className="container margin-top">
                    <button className="mg-r-1 width-100p" onClick={handleSaveSubscription}>
                        {t('pages.subscriptions.actions.save')}
                    </button>
                    <button className="width-100p" onClick={handleCancel}>
                        {t('pages.subscriptions.actions.cancel')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SubscriptionEditor;